import React from "react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Search, Filter, X, Download, Calendar } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";

export default function TransactionFilters({
  filters,
  onFilterChange,
  merchants = [],
  providers = [],
  onExport,
  onReset,
}) {
  const [dateRange, setDateRange] = React.useState({ from: null, to: null });
  const { t } = useLanguage();

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("searchIdRefMerchant")}
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Type */}
        <Select
          value={filters.type || "all"}
          onValueChange={(value) => handleFilterChange("type", value)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t("type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allTypes")}</SelectItem>
            <SelectItem value="DEPOSIT">{t("deposits")}</SelectItem>
            <SelectItem value="WITHDRAWAL">{t("withdrawals")}</SelectItem>
          </SelectContent>
        </Select>

        {/* Status */}
        <Select
          value={filters.status || "all"}
          onValueChange={(value) => handleFilterChange("status", value)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allStatus")}</SelectItem>
            <SelectItem value="PENDING">{t("pending")}</SelectItem>
            <SelectItem value="PROCESSING">{t("processing")}</SelectItem>
            <SelectItem value="SUCCESS">{t("success")}</SelectItem>
            <SelectItem value="FAILED">{t("failed")}</SelectItem>
            <SelectItem value="EXPIRED">{t("expired")}</SelectItem>
            <SelectItem value="CANCELLED">{t("cancelled")}</SelectItem>
          </SelectContent>
        </Select>

        {/* Merchant */}
        <Select
          value={filters.merchant || "all"}
          onValueChange={(value) => handleFilterChange("merchant", value)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t("merchant")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allMerchants")}</SelectItem>
            {merchants.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Provider */}
        <Select
          value={filters.provider || "all"}
          onValueChange={(value) => handleFilterChange("provider", value)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t("provider")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allProviders")}</SelectItem>
            {providers.map((p) => (
              <SelectItem key={p.id} value={p.code}>
                {p.name} ({p.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Calendar className="h-4 w-4" />
              {dateRange.from ? (
                dateRange.to ? (
                  `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                ) : (
                  format(dateRange.from, "MMM d, yyyy")
                )
              ) : (
                t("dateRange")
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                setDateRange(range || { from: null, to: null });
                handleFilterChange("dateFrom", range?.from?.toISOString());
                handleFilterChange("dateTo", range?.to?.toISOString());
              }}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onReset} className="text-slate-500">
            <X className="h-4 w-4 mr-1" />
            {t("clearFilters")}
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={onExport} className="gap-2">
          <Download className="h-4 w-4" />
          {t("exportCsv")}
        </Button>
      </div>
    </div>
  );
}